import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { formatCOPAllowZero } from '../utils/money.js';
import './SuperadminTenants.css';
import './SuperadminFinance.css';

function currentPeriodYm() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function SuperadminFinance() {
    const [periodYm, setPeriodYm] = useState(currentPeriodYm);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [opexForm, setOpexForm] = useState({
        category: 'operacion',
        description: '',
        amountCop: '',
        incurredOn: '',
    });
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.getSuperadminFinance(periodYm);
            setData(res.data || null);
        } catch (e) {
            setError(e.message || 'No se pudo cargar el tablero');
        } finally {
            setLoading(false);
        }
    }, [periodYm]);

    useEffect(() => {
        load();
    }, [load]);

    async function saveCommercial(tenantId) {
        setBusy(true);
        setError('');
        try {
            const res = await api.patchSuperadminTenantCommercial(tenantId, {
                ...editForm,
                periodYm,
            });
            setData(res.data || null);
            setEditId(null);
        } catch (e) {
            setError(e.message || 'No se pudo guardar');
        } finally {
            setBusy(false);
        }
    }

    async function addOpex(e) {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            await api.addSuperadminOpex({
                periodYm,
                category: opexForm.category,
                description: opexForm.description,
                amountCop: Number(opexForm.amountCop),
                incurredOn: opexForm.incurredOn || null,
            });
            setOpexForm({ category: 'operacion', description: '', amountCop: '', incurredOn: '' });
            await load();
        } catch (err) {
            setError(err.message || 'No se pudo registrar el gasto');
        } finally {
            setBusy(false);
        }
    }

    async function generateAll() {
        setBusy(true);
        setError('');
        try {
            await api.generateSuperadminInvoices({ periodYm, onlyActive: true });
            await load();
        } catch (err) {
            setError(err.message || 'No se pudieron generar invoices');
        } finally {
            setBusy(false);
        }
    }

    const summary = data?.summary;

    return (
        <div className="container container-wide animate-in saf-page">
            <header className="sat-header">
                <div>
                    <p className="sat-eyebrow">
                        <Link to="/admin/tenants">← Empresas</Link>
                    </p>
                    <h1>Tablero financiero</h1>
                    <p className="text-muted">
                        Empresas activas, usuarios, vigencias, valor por plan/usuario, gastos de operación e
                        invoices por periodo.
                    </p>
                </div>
                <div className="saf-period">
                    <label className="calc-label" htmlFor="saf-period">
                        Periodo (YYYY-MM)
                    </label>
                    <input
                        id="saf-period"
                        className="input"
                        type="month"
                        value={periodYm}
                        onChange={(e) => setPeriodYm(e.target.value)}
                    />
                </div>
            </header>

            {error ? (
                <div className="glass-card sat-alert" role="alert">
                    {error}
                </div>
            ) : null}

            {loading ? (
                <p className="text-muted">Cargando…</p>
            ) : (
                <>
                    <section className="saf-summary-grid" aria-label="Resumen del periodo">
                        <div className="glass-card saf-kpi">
                            <span className="saf-kpi-label">Empresas activas</span>
                            <strong>
                                {summary?.activeCompanies ?? 0}
                                <span className="saf-kpi-sub"> / {summary?.totalCompanies ?? 0}</span>
                            </strong>
                        </div>
                        <div className="glass-card saf-kpi">
                            <span className="saf-kpi-label">Usuarios activos</span>
                            <strong>{summary?.totalUsers ?? 0}</strong>
                        </div>
                        <div className="glass-card saf-kpi">
                            <span className="saf-kpi-label">Ingresos proyectados</span>
                            <strong>{formatCOPAllowZero(summary?.projectedRevenueCop)}</strong>
                        </div>
                        <div className="glass-card saf-kpi">
                            <span className="saf-kpi-label">Invoices del periodo</span>
                            <strong>{formatCOPAllowZero(summary?.invoiceTotalCop)}</strong>
                        </div>
                        <div className="glass-card saf-kpi">
                            <span className="saf-kpi-label">Gastos operación</span>
                            <strong>{formatCOPAllowZero(summary?.opexTotalCop)}</strong>
                        </div>
                        <div className="glass-card saf-kpi">
                            <span className="saf-kpi-label">Contribución (inv − opex)</span>
                            <strong>{formatCOPAllowZero(summary?.contributionCop)}</strong>
                        </div>
                    </section>

                    <section className="glass-card saf-section">
                        <div className="saf-section-head">
                            <h2>Empresas · plan y vigencia</h2>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={busy}
                                onClick={() => void generateAll()}
                            >
                                Generar invoices del periodo
                            </button>
                        </div>
                        <div className="saf-table-wrap">
                            <table className="saf-table">
                                <thead>
                                    <tr>
                                        <th>Empresa</th>
                                        <th>Perfil</th>
                                        <th>Usuarios</th>
                                        <th>Vigencia</th>
                                        <th>$ / usuario</th>
                                        <th>Fijo plan</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.tenants || []).map((t) => {
                                        const editing = editId === t.id;
                                        return (
                                            <tr key={t.id} className={t.isActive ? '' : 'saf-row-off'}>
                                                <td>
                                                    <strong>{t.name}</strong>
                                                    <div className="text-muted text-sm">
                                                        <code>{t.slug}</code>
                                                        {!t.isActive ? ' · inactiva' : ''}
                                                    </div>
                                                </td>
                                                <td>
                                                    {editing ? (
                                                        <select
                                                            className="input"
                                                            value={editForm.solutionProfile || t.solutionProfile}
                                                            onChange={(e) =>
                                                                setEditForm((f) => ({
                                                                    ...f,
                                                                    solutionProfile: e.target.value,
                                                                }))
                                                            }
                                                        >
                                                            <option value="rotacion">Rotación</option>
                                                            <option value="estado_funcional">
                                                                Estado funcional
                                                            </option>
                                                        </select>
                                                    ) : (
                                                        t.solutionProfile
                                                    )}
                                                </td>
                                                <td>
                                                    {editing ? (
                                                        <input
                                                            className="input"
                                                            style={{ width: 72 }}
                                                            placeholder="auto"
                                                            value={
                                                                editForm.billedSeats ??
                                                                (t.billedSeats ?? '')
                                                            }
                                                            onChange={(e) =>
                                                                setEditForm((f) => ({
                                                                    ...f,
                                                                    billedSeats:
                                                                        e.target.value === ''
                                                                            ? null
                                                                            : e.target.value,
                                                                }))
                                                            }
                                                            title="Cupos facturados (vacío = usuarios activos)"
                                                        />
                                                    ) : (
                                                        <>
                                                            {t.billedSeats != null
                                                                ? `${t.billedSeats} cupos`
                                                                : t.activeUsers}{' '}
                                                            <span className="text-muted text-sm">
                                                                ({t.activeUsers} activos)
                                                            </span>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    {editing ? (
                                                        <div className="saf-inline-dates">
                                                            <input
                                                                type="date"
                                                                className="input"
                                                                value={editForm.validFrom ?? t.validFrom ?? ''}
                                                                onChange={(e) =>
                                                                    setEditForm((f) => ({
                                                                        ...f,
                                                                        validFrom: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                            <input
                                                                type="date"
                                                                className="input"
                                                                value={
                                                                    editForm.validUntil ?? t.validUntil ?? ''
                                                                }
                                                                onChange={(e) =>
                                                                    setEditForm((f) => ({
                                                                        ...f,
                                                                        validUntil: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm">
                                                            {t.validFrom || '—'} → {t.validUntil || '—'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {editing ? (
                                                        <input
                                                            className="input"
                                                            style={{ width: 100 }}
                                                            value={
                                                                editForm.pricePerUserCop ??
                                                                t.pricePerUserCop
                                                            }
                                                            onChange={(e) =>
                                                                setEditForm((f) => ({
                                                                    ...f,
                                                                    pricePerUserCop: e.target.value,
                                                                }))
                                                            }
                                                        />
                                                    ) : (
                                                        formatCOPAllowZero(t.pricePerUserCop)
                                                    )}
                                                </td>
                                                <td>
                                                    {editing ? (
                                                        <input
                                                            className="input"
                                                            style={{ width: 100 }}
                                                            value={
                                                                editForm.planFixedCop ?? t.planFixedCop
                                                            }
                                                            onChange={(e) =>
                                                                setEditForm((f) => ({
                                                                    ...f,
                                                                    planFixedCop: e.target.value,
                                                                }))
                                                            }
                                                        />
                                                    ) : (
                                                        formatCOPAllowZero(t.planFixedCop)
                                                    )}
                                                </td>
                                                <td>
                                                    {editing ? (
                                                        <div className="saf-row-actions">
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary btn-sm"
                                                                disabled={busy}
                                                                onClick={() => void saveCommercial(t.id)}
                                                            >
                                                                Guardar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm"
                                                                onClick={() => setEditId(null)}
                                                            >
                                                                Cancelar
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={() => {
                                                                setEditId(t.id);
                                                                setEditForm({
                                                                    solutionProfile: t.solutionProfile,
                                                                    validFrom: t.validFrom || '',
                                                                    validUntil: t.validUntil || '',
                                                                    pricePerUserCop: t.pricePerUserCop,
                                                                    planFixedCop: t.planFixedCop,
                                                                    billedSeats: t.billedSeats,
                                                                    planCode: t.planCode,
                                                                    billingCycle: t.billingCycle,
                                                                });
                                                            }}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <div className="saf-two-col">
                        <section className="glass-card saf-section">
                            <h2>Gastos de operación</h2>
                            <form className="saf-opex-form" onSubmit={addOpex}>
                                <input
                                    className="input"
                                    placeholder="Categoría"
                                    value={opexForm.category}
                                    onChange={(e) =>
                                        setOpexForm((f) => ({ ...f, category: e.target.value }))
                                    }
                                />
                                <input
                                    className="input"
                                    placeholder="Descripción"
                                    value={opexForm.description}
                                    onChange={(e) =>
                                        setOpexForm((f) => ({ ...f, description: e.target.value }))
                                    }
                                />
                                <input
                                    className="input"
                                    type="number"
                                    min="1"
                                    placeholder="Monto COP"
                                    value={opexForm.amountCop}
                                    onChange={(e) =>
                                        setOpexForm((f) => ({ ...f, amountCop: e.target.value }))
                                    }
                                    required
                                />
                                <input
                                    className="input"
                                    type="date"
                                    value={opexForm.incurredOn}
                                    onChange={(e) =>
                                        setOpexForm((f) => ({ ...f, incurredOn: e.target.value }))
                                    }
                                />
                                <button type="submit" className="btn btn-primary" disabled={busy}>
                                    Registrar gasto
                                </button>
                            </form>
                            <ul className="saf-list">
                                {(data?.opex || []).length === 0 ? (
                                    <li className="text-muted">Sin gastos en este periodo.</li>
                                ) : (
                                    data.opex.map((o) => (
                                        <li key={o.id}>
                                            <span>
                                                <strong>{o.category}</strong>
                                                {o.description ? ` · ${o.description}` : ''}
                                            </span>
                                            <strong>{formatCOPAllowZero(o.amountCop)}</strong>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                disabled={busy}
                                                onClick={async () => {
                                                    setBusy(true);
                                                    try {
                                                        await api.deleteSuperadminOpex(o.id);
                                                        await load();
                                                    } catch (err) {
                                                        setError(err.message);
                                                    } finally {
                                                        setBusy(false);
                                                    }
                                                }}
                                            >
                                                Quitar
                                            </button>
                                        </li>
                                    ))
                                )}
                            </ul>
                        </section>

                        <section className="glass-card saf-section">
                            <h2>Invoices · {periodYm}</h2>
                            <ul className="saf-list">
                                {(data?.invoices || []).length === 0 ? (
                                    <li className="text-muted">
                                        Aún no hay invoices. Use «Generar invoices del periodo».
                                    </li>
                                ) : (
                                    data.invoices.map((inv) => (
                                        <li key={inv.id}>
                                            <span>
                                                <strong>{inv.tenantName}</strong>
                                                <span className="text-muted text-sm">
                                                    {' '}
                                                    · {inv.seats} cupos · {inv.status}
                                                </span>
                                            </span>
                                            <strong>{formatCOPAllowZero(inv.subtotalCop)}</strong>
                                            <select
                                                className="input"
                                                style={{ width: 110 }}
                                                value={inv.status}
                                                disabled={busy}
                                                onChange={async (e) => {
                                                    setBusy(true);
                                                    try {
                                                        await api.setSuperadminInvoiceStatus(
                                                            inv.id,
                                                            e.target.value
                                                        );
                                                        await load();
                                                    } catch (err) {
                                                        setError(err.message);
                                                    } finally {
                                                        setBusy(false);
                                                    }
                                                }}
                                            >
                                                <option value="draft">draft</option>
                                                <option value="issued">issued</option>
                                                <option value="paid">paid</option>
                                                <option value="void">void</option>
                                            </select>
                                        </li>
                                    ))
                                )}
                            </ul>
                        </section>
                    </div>
                </>
            )}
        </div>
    );
}
